import { Component, Signal, TemplateRef, ViewChild, computed, effect, signal, inject } from '@angular/core';

import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { WorkspaceFacade } from '@state';
import { OrganizationProjectsService, OrganizationService, OrganizationUsersService, ProjectService, ProjectUsersService } from '@services';
import { ToastService } from '../../services/toast.service';
import { ModalService } from '../../services/modal.service';
import { Project, Organization, OrganizationProjects } from '@models';

@Component({
  selector: 'app-workspace',
  templateUrl: './workspace.page.html',
  styleUrls: ['./workspace.page.scss'],
  standalone: true,
  imports: [RouterModule, FormsModule, NgbTooltip],
})
export class WorkspacePage {
  private projectUsersService = inject(ProjectUsersService);
  private orgUsersService = inject(OrganizationUsersService);
  private orgProjectsService = inject(OrganizationProjectsService);
  private projectService = inject(ProjectService);
  private organizationService = inject(OrganizationService);
  private userCountsLoading = false;
  @ViewChild('deleteProjectModal') deleteProjectModal!: TemplateRef<any>;
  @ViewChild('deleteOrganizationModal') deleteOrganizationModal!: TemplateRef<any>;
  activeTab: 'projects' | 'organizations' = 'projects';
  search = signal<string>('');
  deletingProject = false;
  deletingOrganization = false;

  projects!: Signal<Project[]>;
  organizations!: Signal<Organization[]>;
  orgProjects!: Signal<OrganizationProjects[]>;

  private projectUserCounts: Record<string, number> = {};
  private orgUserCounts: Record<string, number> = {};
  projectPendingDelete: Project | null = null;
  organizationPendingDelete: Organization | null = null;

  filteredProjects = computed(() => {
    const q = (this.search() || '').toLowerCase();
    const items = this.projects().map((p) => ({
      project: p,
      orgName: (this.getOrganizationNameForProject(p.id) || '').toLowerCase(),
    }));
    const filtered = q
      ? items.filter(({ project, orgName }) =>
          [project?.name, project?.description, orgName].some((v: string) =>
            (v || '').toLowerCase().includes(q)
          )
        )
      : items;
    filtered.sort((a, b) => {
      const byOrg = a.orgName.localeCompare(b.orgName);
      if (byOrg !== 0) return byOrg;
      return (a.project.name || '').localeCompare(b.project.name || '');
    });
    return filtered.map((x) => x.project);
  });

  filteredOrganizations = computed(() => {
    const q = (this.search() || '').toLowerCase();
    const items = this.organizations();
    const filtered = q
      ? items.filter((o: any) =>
          [o?.name, o?.email, o?.website, o?.city, o?.country]
            .some((v: string) => (v || '').toLowerCase().includes(q))
        )
      : items.slice();
    filtered.sort((a: any, b: any) => (a?.name || '').localeCompare(b?.name || ''));
    return filtered;
  });

  constructor(
    private store: WorkspaceFacade,
    private toast: ToastService,
    private router: Router,
    private modal: ModalService,
  ) {
    this.projects = this.store.projects;
    this.organizations = this.store.organizations;
    this.orgProjects = this.store.orgProjects;
    effect(() => {
      if (this.projects().length === 0 && this.organizations().length > 0) {
        this.activeTab = 'organizations';
      }
    });
    // Preload user counts for visible entities
    effect(() => {
      void this.preloadUserCounts(this.projects(), this.organizations());
    });
  }

  private async preloadUserCounts(projects: Project[], organizations: Organization[]): Promise<void> {
    if (this.userCountsLoading) return;
    this.userCountsLoading = true;
    try {
      await Promise.all([
        ...projects
          .filter((p) => !this.projectUserCounts[p.id])
          .map(async (p) => {
            try {
              const res = await this.projectUsersService.find({ where: { projectId: p.id } as any });
              this.projectUserCounts[p.id] = (res.data || []).length;
            } catch {
              this.projectUserCounts[p.id] = 0;
            }
          }),
        ...organizations
          .filter((o) => !this.orgUserCounts[o.id])
          .map(async (o) => {
            try {
              const res = await this.orgUsersService.find({ where: { organizationId: o.id } as any });
              this.orgUserCounts[o.id] = (res.data || []).length;
            } catch {
              this.orgUserCounts[o.id] = 0;
            }
          }),
      ]);
    } finally {
      this.userCountsLoading = false;
    }
  }

  setCurrentProject(projectId: string) {
    const orgId = this.orgProjects().find((op) => op.projectId === projectId)?.organizationId ?? null;
    this.store.selectProject(projectId, orgId);
  }

  getProjectCountForOrg(orgId: string): number {
    try {
      return this.orgProjects().filter((op: any) => op.organizationId === orgId).length || 0;
    } catch {
      return 0;
    }
  }

  getOrganizationNameForProject(projectId: string): string {
    try {
      const mappings = this.orgProjects().filter((op) => op.projectId === projectId);
      if (!mappings?.length) return '';
      const orgs = this.organizations();
      // Prefer a mapping whose organization is already loaded in the store
      const mapping = mappings.find((m) => orgs.some((o) => o.id === m.organizationId)) ?? mappings[0];
      const org = orgs.find((o) => o.id === mapping.organizationId);
      return org?.name || '';
    } catch {
      return '';
    }
  }

  getUserCountForProject(projectId: string): number {
    return this.projectUserCounts[projectId] ?? 0;
  }

  getUserCountForOrganization(orgId: string): number {
    return this.orgUserCounts[orgId] ?? 0;
  }

  openDeleteProjectModal(p: Project) {
    this.projectPendingDelete = p;
    this.modal.open(this.deleteProjectModal, { centered: true, backdrop: 'static', keyboard: true });
  }

  closeDeleteProjectModal() {
    this.projectPendingDelete = null;
    this.deletingProject = false;
    this.modal.dismiss();
  }

  async confirmDeleteProject() {
    if (!this.projectPendingDelete) return;
    try {
      this.deletingProject = true;
      const projectId = this.projectPendingDelete.id;
      const [projUsersRes, orgMappingsRes] = await Promise.all([
        this.projectUsersService.find({ where: { projectId } as any }),
        this.orgProjectsService.find({ where: { projectId } as any }),
      ]);
      const projUsers = projUsersRes.data ?? [];
      const orgMappings = orgMappingsRes.data ?? [];
      await Promise.all([
        ...projUsers.map((link) => this.projectUsersService.delete(link.id)),
        ...orgMappings.map((mapping) => this.orgProjectsService.delete(mapping.id)),
      ]);
      // Finally, delete the project
      await this.projectService.delete(projectId);
      this.store.removeEntities({
        projects: [this.projectPendingDelete],
        projectUsers: projUsers,
        orgProjects: orgMappings,
      });
      this.toast.success('Project deleted', 'Success');
      this.modal.close();
    } catch (e) {
      this.toast.error('Failed to delete project', 'Error');
    } finally {
      this.deletingProject = false;
      this.projectPendingDelete = null;
    }
  }

  async deleteOrganization(orgId: string) {
    // Block deletion if organization has projects
    const numProjects = this.getProjectCountForOrg(orgId);
    if (numProjects > 0) {
      this.toast.error('You cannot delete an organization that still has projects', 'Blocked');
      return;
    }
    const org = this.organizations().find((o) => o.id === orgId) || null;
    if (!org) return;
    this.organizationPendingDelete = org;
    this.modal.open(this.deleteOrganizationModal, { centered: true, backdrop: 'static', keyboard: true });
  }

  editProject(projectId: string) {
    this.router.navigate(['/create-project'], {
      queryParams: { from: 'home', editId: projectId },
    });
  }

  editOrganization(orgId: string) {
    this.router.navigate(['/create-organization'], {
      queryParams: { from: 'home', editId: orgId },
    });
  }

  closeDeleteOrganizationModal() {
    this.organizationPendingDelete = null;
    this.deletingOrganization = false;
    this.modal.dismiss();
  }

  async confirmDeleteOrganization() {
    if (!this.organizationPendingDelete) return;
    try {
      this.deletingOrganization = true;
      const orgId = this.organizationPendingDelete.id;
      const [orgUsersRes, orgMappingsRes] = await Promise.all([
        this.orgUsersService.find({ where: { organizationId: orgId } as any }),
        this.orgProjectsService.find({ where: { organizationId: orgId } as any }),
      ]);
      const orgUsers = orgUsersRes.data ?? [];
      const orgMappings = orgMappingsRes.data ?? [];
      await Promise.all([
        ...orgUsers.map((ou) => this.orgUsersService.delete(ou.id)),
        ...orgMappings.map((mapping) => this.orgProjectsService.delete(mapping.id)),
      ]);
      await this.organizationService.delete(orgId);
      this.store.removeEntities({
        organizations: [this.organizationPendingDelete],
        orgUsers,
        orgProjects: orgMappings,
      });
      this.toast.success('Organization deleted', 'Success');
      this.modal.close();
    } catch (e) {
      this.toast.error('Failed to delete organization', 'Error');
    } finally {
      this.deletingOrganization = false;
      this.organizationPendingDelete = null;
    }
  }
}


